import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Wrench,
  RefreshCw,
  Save,
  Mail,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

function toDatetimeLocal(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function fromDatetimeLocal(value) {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return value;
  }
}

export default function ManutenzioneAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [estimatedEnd, setEstimatedEnd] = useState("");
  const [statusMeta, setStatusMeta] = useState(null);
  const [notifyWhen, setNotifyWhen] = useState("");
  const [notifyDuration, setNotifyDuration] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/maintenance`, { headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      const data = await res.json();
      setEnabled(Boolean(data.enabled));
      setMessage(
        data.message ||
          "Affidya è temporaneamente in manutenzione. Riprova tra poco."
      );
      setEstimatedEnd(toDatetimeLocal(data.estimated_end));
      setStatusMeta({
        updated_at: data.updated_at,
        updated_by: data.updated_by,
        notify_sent_at: data.notify_sent_at,
      });
    } catch (e) {
      toast.error(e.message || "Errore caricamento manutenzione");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/maintenance`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          enabled,
          message: message.trim(),
          estimated_end: fromDatetimeLocal(estimatedEnd),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Salvataggio fallito");
      toast.success(enabled ? "Manutenzione attivata" : "Manutenzione disattivata");
      setStatusMeta({
        updated_at: json.updated_at,
        updated_by: json.updated_by,
        notify_sent_at: json.notify_sent_at,
      });
      if (json.message) setMessage(json.message);
      if (json.estimated_end !== undefined) {
        setEstimatedEnd(toDatetimeLocal(json.estimated_end));
      }
    } catch (e) {
      toast.error(e.message || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleNotify = async () => {
    if (!notifyWhen.trim() || !notifyDuration.trim()) {
      toast.error("Compila quando e durata per l'avviso email");
      return;
    }
    setNotifying(true);
    try {
      const res = await fetch(`${API}/api/admin/maintenance/notify`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          when: notifyWhen.trim(),
          duration: notifyDuration.trim(),
          dry_run: false,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Invio avviso fallito");
      toast.success(json.message || "Avviso email inviato");
      await load();
    } catch (e) {
      toast.error(e.message || "Errore invio avviso");
    } finally {
      setNotifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-600" />
            Manutenzione
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Attiva la modalità manutenzione per bloccare l&apos;accesso utenti
            (tranne super admin). Gli utenti vedono una pagina dedicata.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Aggiorna
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Stato</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-100 bg-amber-50/60 px-4 py-3">
            <div>
              <p className="font-medium text-slate-800">Modalità manutenzione</p>
              <p className="text-xs text-slate-500">
                {enabled ? "Attiva — utenti bloccati" : "Disattiva — piattaforma operativa"}
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div className="space-y-2">
            <Label>Messaggio mostrato agli utenti</Label>
            <Textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Affidya è temporaneamente in manutenzione..."
            />
          </div>

          <div className="space-y-2">
            <Label>Fine stimata (opzionale)</Label>
            <Input
              type="datetime-local"
              value={estimatedEnd}
              onChange={(e) => setEstimatedEnd(e.target.value)}
            />
          </div>

          {statusMeta && (statusMeta.updated_at || statusMeta.notify_sent_at) && (
            <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-500 space-y-1">
              {statusMeta.updated_at && (
                <p>
                  Ultimo aggiornamento:{" "}
                  {new Date(statusMeta.updated_at).toLocaleString("it-IT")}
                  {statusMeta.updated_by ? ` · ${statusMeta.updated_by}` : ""}
                </p>
              )}
              {statusMeta.notify_sent_at && (
                <p>
                  Ultimo avviso email:{" "}
                  {new Date(statusMeta.notify_sent_at).toLocaleString("it-IT")}
                </p>
              )}
            </div>
          )}

          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salva
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Avviso email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">
            Invia un avviso di manutenzione programmata a tutti gli utenti attivi.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Quando</Label>
              <Input
                value={notifyWhen}
                onChange={(e) => setNotifyWhen(e.target.value)}
                placeholder="es. 23 luglio 2026 ore 22:00"
              />
            </div>
            <div className="space-y-2">
              <Label>Durata stimata</Label>
              <Input
                value={notifyDuration}
                onChange={(e) => setNotifyDuration(e.target.value)}
                placeholder="es. circa 2 ore"
              />
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleNotify}
            disabled={notifying}
          >
            {notifying ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2" />
            )}
            Invia avviso email
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
